import { Box, Icon, Skeleton, Theme, Typography } from "@outschool/backpack";
import { removeServiceFee } from "@outschool/business-rules";
import { ActivityEnrollQueryQuery } from "@outschool/gql-frontend-generated";
import { faCircle, faDotCircle } from "@outschool/icons";
import {
  TFunction,
  Trans,
  useLocalPriceDisplay,
  useTranslation,
} from "@outschool/localization";
import { BoxButton } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import React, { useMemo } from "react";

import * as Section from "../../../../shared/Section";
import { useOrderContext } from "../OrderContext";

interface RecurringSelectionProps {
  activity: Pick<
    NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["activity"],
    | "weekly_meetings"
    | "allows_recurring_payment"
    | "is_ongoing_weekly"
    | "isClub"
    | "duration_weeks"
    | "pricingInfo"
    | "price_cents"
    | "isSelfPaced"
    | "isIncludedWithMembership"
  >;
  section?: Pick<
    NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["section"],
    "progress" | "isPublished" | "price_cents" | "allowsLateEnrollment"
  > | null;
}

export default ({ activity, section }: RecurringSelectionProps) => {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\RecurringSelection",
  );
  const { isWeeklyPayment, setIsWeeklyPayment, pricingBreakdown } =
    useOrderContext();
  const isLastWeekOfFixedLengthActivity =
    !!section?.progress &&
    !!activity?.weekly_meetings &&
    section.progress.remainingMeetingCount <= activity.weekly_meetings;
  const isBuyoutSection = !!section && Section.isBuyoutSection(section);

  const isWeeklyPaymentEligible = useMemo(() => {
    return (
      activity.allows_recurring_payment &&
      !activity.is_ongoing_weekly &&
      !activity.isClub &&
      !isLastWeekOfFixedLengthActivity &&
      !!activity.duration_weeks &&
      activity.duration_weeks > 1
    );
  }, [
    activity.allows_recurring_payment,
    activity.is_ongoing_weekly,
    activity.isClub,
    isLastWeekOfFixedLengthActivity,
    activity.duration_weeks,
  ]);

  const shouldShowUpfrontDiscount =
    !isBuyoutSection && !!activity.pricingInfo?.upfrontDiscountPercentage;

  if (!isWeeklyPaymentEligible) {
    return null;
  }

  const upfrontPrice =
    pricingBreakdown?.perEnrollmentUpfrontCents ??
    section?.price_cents ??
    activity.price_cents;

  if (!upfrontPrice || !activity.duration_weeks) {
    return null;
  }

  const weeklyPrice = pricingBreakdown?.perEnrollmentWeeklyCents
    ? removeServiceFee(
        pricingBreakdown?.perEnrollmentWeeklyCents,
        pricingBreakdown?.serviceFeePercentage,
      )
    : (isBuyoutSection
        ? Math.round(upfrontPrice / activity.duration_weeks)
        : activity.pricingInfo?.weeklyPriceCentsForWeeklyPayments) ??
      Math.round(upfrontPrice / activity.duration_weeks);

  return (
    <Box
      sx={{
        marginBottom: "1em",
      }}
    >
      <Typography variant="h5">{t`Payment options`}</Typography>
      {!pricingBreakdown ? (
        <>
          <Skeleton
            variant="rounded"
            width={592}
            height={93}
            sx={{ marginTop: 8, borderRadius: 16 }}
          />
          <Skeleton
            variant="rounded"
            width={592}
            height={93}
            sx={{ marginTop: 8, borderRadius: 16 }}
          />
        </>
      ) : (
        <>
          <PayUpfront
            activity={activity}
            section={section}
            isWeeklyPayment={isWeeklyPayment}
            setIsWeeklyPayment={setIsWeeklyPayment}
            shouldShowUpfrontDiscount={shouldShowUpfrontDiscount}
            upfrontPrice={upfrontPrice}
            weeklyTotalPrice={removeServiceFee(
              pricingBreakdown?.perEnrollmentTotalWeeklyCents,
              pricingBreakdown?.serviceFeePercentage,
            )}
            upfrontTotalPrice={removeServiceFee(
              pricingBreakdown?.perEnrollmentUpfrontCents,
              pricingBreakdown?.serviceFeePercentage,
            )}
          />
          <PayWeekly
            activity={activity}
            isWeeklyPayment={isWeeklyPayment}
            setIsWeeklyPayment={setIsWeeklyPayment}
            weeklyPrice={weeklyPrice}
            shouldShowUpfrontDiscount={shouldShowUpfrontDiscount}
          />
        </>
      )}
    </Box>
  );
};

export function PayWeekly({
  weeklyPrice,
  showPriceInCredits = false,
  isWeeklyPayment,
  shouldShowUpfrontDiscount,
  setIsWeeklyPayment,
  activity,
}: {
  weeklyPrice: number;
  showPriceInCredits?: boolean;
  isWeeklyPayment: boolean;
  shouldShowUpfrontDiscount: boolean;
  setIsWeeklyPayment: (isWeekly: boolean) => void;
  activity: RecurringSelectionProps["activity"];
}) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\RecurringSelection",
  );
  const { renderCurrencyString } = useLocalPriceDisplay();
  return (
    <PaymentOption
      title={t`Pay Weekly`}
      price={
        showPriceInCredits
          ? (weeklyPrice as unknown as string)
          : renderCurrencyString({
              priceInCents: weeklyPrice,
              showCurrencyCode: false,
            })
      }
      showPriceInCredits={showPriceInCredits}
      isUpFront={false}
      isSelected={isWeeklyPayment}
      shouldShowUpfrontDiscount={shouldShowUpfrontDiscount}
      onSelect={() => setIsWeeklyPayment(true)}
    >
      <Typography variant="body1">
        <WeeklyPriceDescription
          activity={activity}
          t={t}
          weeklyPrice={weeklyPrice}
          showPriceInCredits={showPriceInCredits}
        />
      </Typography>
    </PaymentOption>
  );
}

export function PayUpfront({
  shouldShowUpfrontDiscount,
  isWeeklyPayment,
  setIsWeeklyPayment,
  activity,
  section,
  upfrontPrice,
  weeklyTotalPrice,
  upfrontTotalPrice,
  showPriceInCredits = false,
}: {
  shouldShowUpfrontDiscount: boolean;
  isWeeklyPayment: boolean;
  setIsWeeklyPayment: (weekly: boolean) => void;
  activity: UpfrontPriceDescriptionProps["activity"];
  section: UpfrontPriceDescriptionProps["section"];
  upfrontPrice: number;
  weeklyTotalPrice: number;
  upfrontTotalPrice: number;
  showPriceInCredits?: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\RecurringSelection",
  );
  const { renderCurrencyString } = useLocalPriceDisplay();

  const pricingInfo = {
    upfrontDiscountPercentage:
      activity.pricingInfo?.upfrontDiscountPercentage ?? 0,
    weeklyTotalPrice,
    upfrontTotalPrice,
  };

  return (
    <PaymentOption
      title={t`Pay Upfront`}
      price={
        showPriceInCredits
          ? shouldShowUpfrontDiscount
            ? activity.pricingInfo!.weeklyPriceCreditsForUpfrontPayment.toFixed(
                0,
              )
            : upfrontPrice.toFixed(0)
          : shouldShowUpfrontDiscount
          ? renderCurrencyString({
              priceInCents:
                activity.pricingInfo!.weeklyPriceCentsForUpfrontPayment,
              showCurrencyCode: false,
            })
          : renderCurrencyString({
              priceInCents: upfrontPrice,
              showCurrencyCode: false,
            })
      }
      showPriceInCredits={showPriceInCredits}
      isUpFront={true}
      isSelected={!isWeeklyPayment}
      pricingInfo={pricingInfo!}
      shouldShowUpfrontDiscount={
        activity.isIncludedWithMembership ? false : shouldShowUpfrontDiscount
      }
      onSelect={() => setIsWeeklyPayment(false)}
    >
      <Typography variant="body1">
        <UpfrontPriceDescription activity={activity} section={section} t={t} />
      </Typography>
    </PaymentOption>
  );
}

interface WeeklyPriceDescriptionProps {
  activity: RecurringSelectionProps["activity"];
  weeklyPrice: number;
  showPriceInCredits?: boolean;
  t: TFunction;
}

function WeeklyPriceDescription({
  activity,
  t,
  weeklyPrice,
  showPriceInCredits = false,
}: WeeklyPriceDescriptionProps) {
  const { isPricingBreakdownLoading, pricingBreakdown } = useOrderContext();
  const { renderCurrencyString } = useLocalPriceDisplay();
  const hasProratedEnrollments = !!pricingBreakdown?.hasProratedEnrollments;
  const currentWeekPrice = showPriceInCredits
    ? pricingBreakdown?.perEnrollmentCurrentWeekCredits
    : removeServiceFee(
        pricingBreakdown?.perEnrollmentCurrentWeekCents,
        pricingBreakdown?.serviceFeePercentage,
      );
  const isPartialWeek =
    typeof currentWeekPrice === "number" && currentWeekPrice !== weeklyPrice;

  if (!pricingBreakdown && isPricingBreakdownLoading) {
    return <br />;
  }

  if (hasProratedEnrollments) {
    const weeklyPriceString = showPriceInCredits
      ? (weeklyPrice as unknown as string)
      : renderCurrencyString({
          priceInCents: weeklyPrice,
          showCurrencyCode: false,
        });

    if (isPartialWeek) {
      const partialWeekPriceString = showPriceInCredits
        ? (currentWeekPrice as unknown as string)
        : renderCurrencyString({
            priceInCents: currentWeekPrice,
            showCurrencyCode: false,
          });

      return (
        <Trans t={t}>
          This week, pay{" "}
          <Typography emphasized>{{ partialWeekPriceString }}.</Typography>
          <br />
          Then, pay <Typography emphasized>
            {{ weeklyPriceString }}
          </Typography>{" "}
          weekly until class ends
        </Trans>
      );
    }

    return (
      <Trans t={t}>
        Pay weekly <Typography emphasized>{{ weeklyPriceString }}</Typography>{" "}
        until class ends
      </Trans>
    );
  }

  const durationWeeks = Number(activity.duration_weeks);

  if (activity.isSelfPaced) {
    return (
      <>
        {t("Receive new lesson(s) every week for 1 week.", {
          durationWeeks,
          count: durationWeeks,
          defaultValue_plural:
            "Receive new lesson(s) every week for {{ durationWeeks }} weeks.",
        })}
      </>
    );
  }
  return (
    <>
      {t("Over 1 week, cancel anytime", {
        durationWeeks,
        count: durationWeeks,
        defaultValue_plural: "Over {{ durationWeeks }} weeks, cancel anytime",
      })}
    </>
  );
}

interface UpfrontPriceDescriptionProps {
  activity: RecurringSelectionProps["activity"];
  section?: RecurringSelectionProps["section"];
  t: TFunction;
}

function UpfrontPriceDescription({
  activity,
  section,
  t,
}: UpfrontPriceDescriptionProps) {
  if (!!section?.allowsLateEnrollment && !!section?.progress) {
    return (
      <>
        {t("Pay only for the remaining {{remainingMeetingCount}} meeting", {
          remainingMeetingCount: section.progress?.remainingMeetingCount,
          count: section.progress?.remainingMeetingCount,
          defaultValue_plural:
            "Pay only for the remaining {{remainingMeetingCount}} meetings",
        })}
      </>
    );
  }

  const durationWeeks = Number(activity.duration_weeks);

  if (activity.isSelfPaced) {
    return <>{t("Get full access to all lessons immediately")}</>;
  }
  return (
    <>
      {t("Covers the full week", {
        durationWeeks,
        count: durationWeeks,
        defaultValue_plural: "Covers the full {{durationWeeks}} weeks",
      })}
    </>
  );
}

interface PaymentOptionProps {
  children: React.ReactNode;
  title: string;
  price: string;
  showPriceInCredits: boolean;
  isUpFront: boolean;
  isSelected: boolean;
  pricingInfo?: {
    weeklyTotalPrice: number;
    upfrontTotalPrice: number;
    upfrontDiscountPercentage: number;
  };
  shouldShowUpfrontDiscount: boolean;
  onSelect: () => void;
}

const PaymentOption = ({
  children,
  title,
  price,
  showPriceInCredits,
  isUpFront,
  isSelected,
  pricingInfo,
  shouldShowUpfrontDiscount,
  onSelect,
}: PaymentOptionProps) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\RecurringSelection",
  );

  const { renderCurrencyString } = useLocalPriceDisplay();

  const formatPrice = (price: number) => {
    return showPriceInCredits
      ? t("{{count}} credit", {
          defaultValue_plural: "{{count}} credits",
          count: price,
        })
      : renderCurrencyString({
          priceInCents: price,
          showCurrencyCode: false,
        });
  };

  return (
    <BoxButton
      onClick={() => !isSelected && onSelect()}
      sx={(theme: Theme) => ({
        position: "relative",
        width: "100%",
        cursor: "pointer",
        marginTop: 8,
        padding: "16px",
        borderRadius: "xlarge",
        border: "1px solid",
        borderColor: isSelected ? "peacock500" : "gray300",
        background: theme.palette.common.white,
        color: "#3C3C3C",
        transition: "all 0.15s",
        "&:hover": {
          borderColor: isSelected ? "peacock700" : "gray400",
          boxShadow: "0px 0px 4px 0px #0000000A, 0px 8px 16px 0px #00000014",
        },
      })}
      data-test-id="recurring-payment-option"
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {isSelected ? (
            <Icon
              icon={faDotCircle}
              size="small"
              sx={{
                color: "primary.main",
              }}
            />
          ) : (
            <Icon icon={faCircle} size="small" sx={{ color: "grey.600" }} />
          )}
          <Typography
            emphasized
            variant="h6"
            sx={{
              marginRight: "0.5em",
              color: isSelected ? "primary.700" : "grey.700",
            }}
          >
            {title}
          </Typography>
        </Box>
        <Box
          flex
          sx={{
            flexDirection: "row",
            alignItems: "baseline",
            justifyContent: isMobile && "flex-end",
            textAlign: isMobile && "right",
          }}
        >
          <Typography
            variant="h6"
            emphasized
            data-test-id="price-Display"
            sx={{
              color: isSelected ? "primary.700" : "grey.700",
            }}
          >
            {price}
          </Typography>
          {showPriceInCredits && (
            <Typography
              variant="body2"
              sx={{
                color: isSelected ? "primary.700" : "grey.700",
                marginLeft: "0.25em",
              }}
            >
              {t("credits")}
            </Typography>
          )}
          {shouldShowUpfrontDiscount && (
            <Typography
              variant="body2"
              sx={{
                color: isSelected ? "primary.700" : "grey.700",
              }}
            >
              {t("/week")}
            </Typography>
          )}
        </Box>
      </Box>
      <Box
        flex
        sx={{
          justifyContent: "space-between",
          alignItems: !isMobile && "center",
          marginLeft: "28px",
        }}
      >
        {
          <Typography
            variant="subtitle1"
            sx={{
              marginTop: "small",
            }}
          >
            {children}
          </Typography>
        }
        {shouldShowUpfrontDiscount && pricingInfo && isUpFront && (
          <Box
            flex
            sx={{
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-end" : "baseline",
              textAlign: "right",
              whiteSpace: "nowrap",
              gap: !isMobile && "8px",
              color: !isUpFront && "grey.700",
            }}
          >
            <Typography variant="body2">
              {t("{{Price}} total", {
                Price: formatPrice(pricingInfo.upfrontTotalPrice),
              })}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "text.disabled",
                textDecoration: "line-through",
              }}
            >
              {formatPrice(pricingInfo.weeklyTotalPrice)}
            </Typography>
          </Box>
        )}
      </Box>
      {shouldShowUpfrontDiscount && isUpFront && pricingInfo && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Typography
            variant="subtitle2"
            sx={{ color: "success.600" }}
            data-test-id="upfront-discount-display"
          >
            {t("Save {{discountPercentage}}% ({{costSaved}} savings)", {
              discountPercentage: pricingInfo.upfrontDiscountPercentage,
              costSaved: formatPrice(
                pricingInfo.weeklyTotalPrice - pricingInfo.upfrontTotalPrice,
              ),
            })}
          </Typography>
        </Box>
      )}
    </BoxButton>
  );
};
